// DashboardLayout: reads the dashboard config and renders the grid.
//
// The active dashboard view controls both the grid geometry and the base panel
// assignment. Each panel is placed into its grid area by index:
// panels[0] -> area "a", panels[1] -> "b", etc.

import React, { useState } from 'react';
import useDashboardConfig from '../../Store/dashboardConfigStore';
import { getTemplate, AREA_NAMES } from './layoutTemplates';
import { DASHBOARD_VIEW_LIST } from './dashboardViews';
import WidgetPanel from './WidgetPanel';
import ConfigModal from './ConfigModal';
import TopBar from './TopBar';
import '../../Styles/dashboard.css';
import '../../Styles/configModal.css';

export default function DashboardLayout({
  memberCredentials = null,
  onRecoverReplayHistory = null,
  replayDates = [],
  replayDateDiscoveryReady = true,
  replayControlReady = replayDateDiscoveryReady,
  onReplayDateSelect = null,
}) {
  const activeViewId = useDashboardConfig((s) => s.activeViewId);
  const layoutId = useDashboardConfig((s) => s.layoutId);
  const panels = useDashboardConfig((s) => s.panels);

  const applyDashboardView = useDashboardConfig((s) => s.applyDashboardView);
  const setActiveTab = useDashboardConfig((s) => s.setActiveTab);

  const [configOpen, setConfigOpen] = useState(false);

  const template = getTemplate(layoutId);
  const visiblePanels = panels.slice(0, template.panelCount);

  const handleDashboardViewChange = (view) => {
    if (view.id === activeViewId) return;

    applyDashboardView(view);

    // On stacked responsive layouts, always begin the newly selected
    // dashboard view at Panel 1 instead of preserving the previous
    // view's vertical scroll position.
    if (window.matchMedia('(max-width: 1249px)').matches) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto',
          });
        });
      });
    }
  };

  const dashboardViewTabs = (
    <div className="widget-tabs dashboard-view-tabs">
      {DASHBOARD_VIEW_LIST.map((view) => (
        <button
          key={view.id}
          type="button"
          className={`widget-tab ${view.id === activeViewId ? 'is-active' : ''}`}
          onClick={() => handleDashboardViewChange(view)}
          aria-pressed={view.id === activeViewId}
          aria-label={view.label}
          title={view.label}
        >
          {view.label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <TopBar
        onOpenConfig={() => setConfigOpen(true)}
        onRecoverReplayHistory={onRecoverReplayHistory}
        replayDates={replayDates}
        replayDateDiscoveryReady={replayControlReady}
      />

      <div
        className={`dashboard-grid ${template.className}${
          configOpen ? ' dashboard-grid--paused' : ''
        }`}
      >
        {visiblePanels.map((panel, i) => (
          <div
            key={panel.id}
            className="dashboard-slot"
            style={{ gridArea: AREA_NAMES[i] }}
          >
            <WidgetPanel
              panel={panel}
              onTabChange={setActiveTab}
              configPaused={configOpen}
              memberCredentials={memberCredentials}
              headerContent={i === 0 ? dashboardViewTabs : null}
            />
          </div>
        ))}
      </div>

      {configOpen && (
        <ConfigModal
          memberCredentials={memberCredentials}
          replayDates={replayDates}
          replayDateDiscoveryReady={replayDateDiscoveryReady}
          onReplayDateSelect={onReplayDateSelect}
          onClose={() => setConfigOpen(false)}
        />
      )}
    </>
  );
}
